"use client";
import AppIcon from "../../../App/AppIcon";
import { useState } from "react";
import { Controller, FieldErrors } from "react-hook-form";
import "./style.css";

type Props = {
  label?: string;
  name: string;
  control: any;
  error?: string | FieldErrors;
  className?: string;
  containerClassName?: string;
  onlyNumber?: boolean;
  maxLength?: number;
  type?:
    | "normal"
    | "number"
    | "hour"
    | "password"
    | "currency"
    | "time"
    | "date"
    | "phone";
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  onCustomChange?: (value: string) => void;
  handleOpenHelper?: () => void;
  helperText?: string;
  prefix?: string;
};

const ControlledText = ({
  label,
  name,
  control,
  error,
  className,
  containerClassName,
  onlyNumber,
  maxLength,
  type,
  required,
  placeholder,
  disabled,
  onCustomChange,
  handleOpenHelper,
  helperText,
  prefix,
}: Props) => {
  const [seePass, setSeePass] = useState(false);

  const getErrorMessage = (): string | undefined => {
    if (!error) return undefined;

    if (typeof error === "string") {
      return error;
    }

    // Navega pela estrutura aninhada de erros usando o caminho do name
    // Ex: "address.street" -> error.address.street
    if (typeof error === "object") {
      const nameParts = name.split(".");
      let currentError: any = error;

      for (const part of nameParts) {
        if (currentError && typeof currentError === "object" && part in currentError) {
          currentError = currentError[part];
        } else {
          return undefined;
        }
      }

      return currentError?.message as string | undefined;
    }

    return undefined;
  };

  const errorMessage = getErrorMessage();

  const handleValidation = (value: string) => {
    // Se temos um prefixo, removemos ele temporariamente para validação
    let cleanValue = value;
    if (prefix && value.startsWith(prefix)) {
      cleanValue = value.substring(prefix.length);
    }

    if ((onlyNumber || type === "number") && !/^[0-9]*$/.test(cleanValue)) {
      return false;
    }

    if (type === "currency") {
      if (cleanValue === "") return prefix ? prefix : cleanValue;
      if (!/^\d*(,\d{0,2})?$/.test(cleanValue)) {
        return false;
      }
    }

    if (type === "hour") {
      if (cleanValue.length > 5) {
        return false;
      }

      if (cleanValue.length === 2 && !cleanValue.includes(":")) {
        cleanValue = `${cleanValue}:`;
      }

      if (cleanValue.length === 2 && cleanValue.includes(":")) {
        return false;
      }

      if (cleanValue.length === 3 && cleanValue.includes(":")) {
        cleanValue = `${cleanValue[0]}${cleanValue[2]}:`;
      }

      if (cleanValue.length === 5 && cleanValue.includes(":")) {
        cleanValue = `${cleanValue[0]}${cleanValue[1]}:${cleanValue[3]}${cleanValue[4]}`;
      }
    }

    if (type === "phone") {
      // Remove tudo que não for número
      let digits = cleanValue.replace(/\D/g, "");
      if (digits.length > 11) digits = digits.slice(0, 11);
      let masked = "";
      if (digits.length > 0) masked = digits.slice(0, 2);
      if (digits.length >= 3) masked += " " + digits.slice(2, 7);
      if (digits.length >= 8) masked += "-" + digits.slice(7, 11);
      cleanValue = masked;
    }

    return prefix ? prefix + cleanValue : cleanValue;
  };

  return (
    <>
      <div className={`controlled-text-container ${containerClassName || ""}`}>
        <label
          htmlFor={name}
          className={`controlled-text-label ${errorMessage ? "error" : ""}`}
        >
          {label}
          {required && <span className="controlled-text-required"> *</span>}
        </label>
        {/* {helperText && <Tooltip target={`.helper-${name}`} content={helperText} />} */}
        {handleOpenHelper && (
          <AppIcon
            icon="mdi:help-circle-outline"
            className={`controlled-text-helper helper-${name}`}
            width={30}
            onClick={handleOpenHelper}
          />
        )}
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, value } }) => {
            const displayValue =
              prefix && value && !value.startsWith(prefix)
                ? prefix + value
                : value || prefix || "";

            return (
              <input
                className={`controlled-text-input ${className || ""} ${
                  errorMessage ? "error" : ""
                } ${disabled ? "disabled" : ""} ${prefix ? "with-prefix" : ""}`}
                id={name}
                value={displayValue}
                type={
                  type === "password"
                    ? seePass
                      ? "text"
                      : type
                    : type === "time" || type === "date"
                    ? type
                    : "text"
                }
                onChange={(e) => {
                  let newValue = e.target.value;

                  if (prefix && !newValue.startsWith(prefix)) {
                    if (newValue.length > 0) {
                      newValue = prefix + newValue;
                    }
                  }

                  const validatedValue = handleValidation(newValue);
                  if (validatedValue !== false) {
                    onChange(validatedValue);
                    onCustomChange && onCustomChange(validatedValue);
                  }
                }}
                onFocus={(e) => {
                  // Quando o input recebe foco, garantir que o cursor fique após o prefixo
                  if (prefix) {
                    const cursorPosition = prefix.length;
                    setTimeout(() => {
                      e.target.setSelectionRange(
                        cursorPosition,
                        cursorPosition
                      );
                    }, 0);
                  }
                }}
                maxLength={
                  maxLength
                    ? prefix
                      ? maxLength + prefix.length
                      : maxLength
                    : undefined
                }
                placeholder={placeholder}
                disabled={disabled}
              />
            );
          }}
        />
        {type === "password" && (
          <AppIcon
            icon={seePass ? "mdi:eye-off" : "mdi:eye"}
            className="controlled-text-password-toggle"
            width={30}
            onClick={() => setSeePass(!seePass)}
          />
        )}
        {errorMessage && (
          <small className="controlled-text-error">{errorMessage}</small>
        )}
      </div>
    </>
  );
};

export default ControlledText;
